/**
 * PDF email recipients management — per-client mailing list for the
 * scheduled monthly PDF reports.
 *
 * Rendered as a sub-view inside the Users tab of the Lupa Admin panel.
 * dwa_admin only — RagAdminPage already gates entry; the endpoints
 * also enforce the role.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listEmailRecipients,
  createEmailRecipient,
  deleteEmailRecipient,
  type EmailRecipient,
  type ReportType,
} from '../../services/adminApi';

const CLIENT_OPTIONS: { id: string; label: string }[] = [
  { id: 'morningstar', label: 'MorningStar Senior Living' },
  { id: 'plc', label: 'Priority Life Care' },
  { id: 'dwa-internal', label: 'DWA Internal' },
];

const REPORT_TYPE_OPTIONS: { id: ReportType; label: string }[] = [
  { id: 'weekly_scorecard', label: 'Weekly Scorecard (DWA Internal)' },
  { id: 'weekly_plc_report', label: 'Weekly PLC Report' },
  { id: 'monthly', label: 'Monthly Client Report' },
  { id: 'backup_alert', label: 'Backup alerts (DWA Internal)' },
];

const card: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  padding: '1.25rem 1.5rem',
};

const input: React.CSSProperties = {
  fontSize: '0.85rem',
  padding: '0.4rem 0.55rem',
  borderRadius: 4,
  border: '1px solid #cbd5e1',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const label: React.CSSProperties = {
  fontSize: '0.7rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#64748b',
  fontWeight: 600,
  marginBottom: '0.25rem',
  display: 'block',
};

const button = (variant: 'primary' | 'secondary' | 'danger'): React.CSSProperties => ({
  fontSize: '0.78rem',
  fontWeight: 600,
  padding: '0.4rem 0.85rem',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  background: variant === 'primary' ? '#fec35c' : 'transparent',
  color: variant === 'primary' ? '#1a202c' : variant === 'danger' ? '#dc2626' : '#1a202c',
  border: `1px solid ${variant === 'primary' ? '#fec35c' : variant === 'danger' ? '#dc2626' : '#cbd5e1'}`,
});

export const EmailRecipientsPanel: React.FC = () => {
  const [recipients, setRecipients] = useState<EmailRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-recipient form state
  const [draftEmail, setDraftEmail] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftClient, setDraftClient] = useState<string>('dwa-internal');
  const [draftReportType, setDraftReportType] = useState<ReportType>('weekly_scorecard');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listEmailRecipients();
      setRecipients(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleAdd = async () => {
    const email = draftEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setAddError('Enter a valid email address.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await createEmailRecipient({
        client_id: draftClient,
        email,
        display_name: draftName.trim() || undefined,
        report_type: draftReportType,
      });
      setDraftEmail('');
      setDraftName('');
      await reload();
    } catch (e) {
      setAddError(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (r: EmailRecipient) => {
    if (!window.confirm(`Remove ${r.email} from ${r.client_id} ${r.report_type} recipients?`)) return;
    try {
      await deleteEmailRecipient(r.id);
      await reload();
    } catch (e) {
      alert(`Failed to remove: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Group recipients by (report_type, client_id) for display.
  const recipientsGrouped = useMemo(() => {
    const map: Record<string, Record<string, EmailRecipient[]>> = {};
    for (const r of recipients) {
      if (!map[r.report_type]) map[r.report_type] = {};
      if (!map[r.report_type][r.client_id]) map[r.report_type][r.client_id] = [];
      map[r.report_type][r.client_id].push(r);
    }
    return map;
  }, [recipients]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1a202c' }}>PDF Email Recipients</h2>
        <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0.3rem 0 0' }}>
          Recipients here receive the scheduled monthly PDF report for their assigned client
          on the 1st of each month. Add or remove addresses below.
        </p>
      </div>

      {/* Add form */}
      <div style={card}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: '#1a202c' }}>Add Recipient</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}>
          <div>
            <label style={label} htmlFor="recipient-report-type">Report</label>
            <select
              id="recipient-report-type"
              style={{ ...input, width: '100%', cursor: 'pointer' }}
              value={draftReportType}
              onChange={(e) => setDraftReportType(e.target.value as ReportType)}
            >
              {REPORT_TYPE_OPTIONS.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="recipient-client">Client</label>
            <select
              id="recipient-client"
              style={{ ...input, width: '100%', cursor: 'pointer' }}
              value={draftClient}
              onChange={(e) => setDraftClient(e.target.value)}
            >
              {CLIENT_OPTIONS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="recipient-email">Email</label>
            <input
              id="recipient-email"
              type="email"
              style={{ ...input, width: '100%' }}
              value={draftEmail}
              onChange={(e) => setDraftEmail(e.target.value)}
              placeholder="name@digitalwolfagency.com"
            />
          </div>
          <div>
            <label style={label} htmlFor="recipient-name">Display Name (optional)</label>
            <input
              id="recipient-name"
              type="text"
              style={{ ...input, width: '100%' }}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="e.g. Jon"
            />
          </div>
          <button
            type="button"
            style={button('primary')}
            onClick={handleAdd}
            disabled={adding}
          >
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </div>
        {addError && (
          <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0.5rem 0 0' }}>{addError}</p>
        )}
      </div>

      {/* Existing recipients grouped by report_type → client */}
      <div style={card}>
        {loading && <p style={{ color: '#64748b', margin: 0 }}>Loading…</p>}
        {error && <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>}
        {!loading && !error && recipients.length === 0 && (
          <p style={{ color: '#94a3b8', margin: 0 }}>No recipients configured yet.</p>
        )}
        {!loading && !error && REPORT_TYPE_OPTIONS.map((rt) => {
          const byClient = recipientsGrouped[rt.id] || {};
          const totalRows = Object.values(byClient).reduce((a, arr) => a + arr.length, 0);
          if (totalRows === 0) return null;
          return (
            <div key={rt.id} style={{ marginBottom: '1.5rem' }}>
              <h3 style={{
                fontSize: '0.95rem', margin: '0 0 0.6rem',
                color: '#1a202c', fontWeight: 700,
                borderBottom: '1px solid #e2e8f0', paddingBottom: '0.35rem',
              }}>
                {rt.label}
                <span style={{ marginLeft: '0.5rem', fontWeight: 400, fontSize: '0.78rem', color: '#94a3b8' }}>
                  ({totalRows})
                </span>
              </h3>
              {CLIENT_OPTIONS.map((c) => {
                const rows = byClient[c.id] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={c.id} style={{ marginBottom: '0.75rem', paddingLeft: '0.5rem' }}>
                    <h4 style={{
                      fontSize: '0.8rem', margin: '0 0 0.4rem',
                      color: '#4a5568', fontWeight: 600,
                    }}>
                      {c.label}
                      <span style={{ marginLeft: '0.4rem', fontWeight: 400, fontSize: '0.72rem', color: '#94a3b8' }}>
                        ({rows.length})
                      </span>
                    </h4>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {rows.map((r) => (
                        <li
                          key={r.id}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '0.45rem 0.65rem',
                            borderBottom: '1px solid #f1f5f9',
                            fontSize: '0.85rem',
                          }}
                        >
                          <div>
                            <strong style={{ color: '#1a202c' }}>{r.email}</strong>
                            {r.display_name && (
                              <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>
                                ({r.display_name})
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            style={button('danger')}
                            onClick={() => handleRemove(r)}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmailRecipientsPanel;
